import { addClass, removeClass, outerHeight } from '../../helpers/dom/element';
import { CONDITION_OPTIONS, BLANK_LABEL } from './conditions';

/** 面板被迫压缩时，值列表至少保留的高度，保证还能看到几行选项 */
const MIN_VALUE_LIST_HEIGHT = 80;

/**
 * 列筛选下拉面板。
 *
 * 纯原生 DOM 实现，不依赖任何 UI 组件库，以保证该内核可在
 * element-ui / antd / 无 UI 库 的任意宿主项目中直接复用。
 *
 * 面板结构：
 *   [条件下拉] [参数输入 x0~2]
 *   [搜索框]
 *   [全选] [值列表(复选)]
 *   [清除筛选] [取消] [确定]
 */
class FilterPanel {
  /**
   * @param {Object} options 回调集合
   * @param {Function} options.onConfirm (state) => void
   * @param {Function} options.onClear () => void
   */
  constructor(options = {}) {
    this.options = options;
    this.element = null;
    this.column = -1;

    /** 当前面板内的草稿状态 */
    this.draft = { selectedValues: null, condition: null };
    /** 该列全部去重值 */
    this.distinctValues = [];
    /** 搜索关键字 */
    this.keyword = '';

    this.build();
  }

  /**
   * 构建面板 DOM（只构建一次，后续复用）。
   */
  build() {
    const root = document.createElement('div');

    root.className = 'htFilterPanel';
    root.style.display = 'none';
    // 阻止面板内的交互冒泡到 handsontable，避免触发选区变化 / 关闭面板
    ['mousedown', 'mouseup', 'click', 'dblclick', 'contextmenu'].forEach((name) => {
      root.addEventListener(name, event => event.stopPropagation());
    });

    // ---- 条件筛选区 ----
    const conditionBox = document.createElement('div');

    conditionBox.className = 'htFilterPanelSection';

    this.conditionSelect = document.createElement('select');
    this.conditionSelect.className = 'htFilterConditionSelect';
    CONDITION_OPTIONS.forEach((item) => {
      const option = document.createElement('option');

      option.value = item.name;
      option.textContent = item.label;
      this.conditionSelect.appendChild(option);
    });
    this.conditionSelect.addEventListener('change', () => this.onConditionChange());

    this.argInputs = [0, 1].map(() => {
      const input = document.createElement('input');

      input.type = 'text';
      input.className = 'htFilterConditionArg';
      input.style.display = 'none';

      return input;
    });

    conditionBox.appendChild(this.conditionSelect);
    this.argInputs.forEach(input => conditionBox.appendChild(input));

    // ---- 搜索框 ----
    const searchBox = document.createElement('div');

    searchBox.className = 'htFilterPanelSection';
    this.searchInput = document.createElement('input');
    this.searchInput.type = 'text';
    this.searchInput.className = 'htFilterSearchInput';
    this.searchInput.placeholder = '搜索';
    this.searchInput.addEventListener('input', () => {
      this.keyword = this.searchInput.value.trim().toLowerCase();
      this.renderValueList();
    });
    searchBox.appendChild(this.searchInput);

    // ---- 全选 ----
    const selectAllBox = document.createElement('div');

    selectAllBox.className = 'htFilterPanelSection htFilterSelectAll';
    this.selectAllCheckbox = document.createElement('input');
    this.selectAllCheckbox.type = 'checkbox';
    this.selectAllCheckbox.id = `htFilterSelectAll_${Math.random().toString(36).slice(2)}`;
    this.selectAllCheckbox.addEventListener('change', () => this.onSelectAllChange());

    const selectAllLabel = document.createElement('label');

    selectAllLabel.textContent = '全选';
    selectAllLabel.setAttribute('for', this.selectAllCheckbox.id);

    selectAllBox.appendChild(this.selectAllCheckbox);
    selectAllBox.appendChild(selectAllLabel);

    // ---- 值列表 ----
    this.valueList = document.createElement('div');
    this.valueList.className = 'htFilterValueList';

    // ---- 操作按钮 ----
    const footer = document.createElement('div');

    footer.className = 'htFilterPanelFooter';

    this.clearButton = this.createButton('清除筛选', 'htFilterBtnLink', () => {
      if (this.options.onClear) {
        this.options.onClear(this.column);
      }
    });
    this.cancelButton = this.createButton('取消', 'htFilterBtn', () => this.hide());
    this.confirmButton = this.createButton('确定', 'htFilterBtn htFilterBtnPrimary', () => {
      if (this.options.onConfirm) {
        this.options.onConfirm(this.column, this.collectState());
      }
    });

    const footerRight = document.createElement('div');

    footerRight.appendChild(this.cancelButton);
    footerRight.appendChild(this.confirmButton);
    footer.appendChild(this.clearButton);
    footer.appendChild(footerRight);

    root.appendChild(conditionBox);
    root.appendChild(searchBox);
    root.appendChild(selectAllBox);
    root.appendChild(this.valueList);
    root.appendChild(footer);

    this.element = root;
  }

  createButton(text, className, handler) {
    const button = document.createElement('button');

    button.type = 'button';
    button.className = className;
    button.textContent = text;
    button.addEventListener('click', handler);

    return button;
  }

  /**
   * 打开面板。
   *
   * 面板统一挂载到 document.body 并使用 fixed 定位：表格容器（以及宿主页面的
   * 卡片/表单容器）普遍带 overflow: hidden，若把面板挂在容器内部，一旦面板高度
   * 超过容器可视区就会被裁掉半截。挂到 body 后彻底不受任何祖先 overflow 影响。
   *
   * @param {Number} column 可视列下标
   * @param {HTMLElement} th 该列表头元素（用于定位）
   * @param {String[]} distinctValues 该列全部去重值（已归一化）
   * @param {Object} state 该列当前筛选状态
   */
  show(column, th, distinctValues, state) {
    this.column = column;
    this.distinctValues = distinctValues;
    this.keyword = '';
    this.searchInput.value = '';

    // 深拷贝一份草稿，取消时不影响原状态
    this.draft = {
      selectedValues: state && Array.isArray(state.selectedValues)
        ? state.selectedValues.slice()
        : null,
      condition: state && state.condition
        ? { name: state.condition.name, args: (state.condition.args || []).slice() }
        : null,
    };

    this.conditionSelect.value = this.draft.condition ? this.draft.condition.name : 'none';
    this.syncArgInputs();
    this.renderValueList();

    if (this.element.parentNode !== document.body) {
      document.body.appendChild(this.element);
    }

    this.element.style.display = 'block';
    this.position(th);
  }

  /**
   * 基于视口坐标（fixed）定位到表头下方，并做边界收敛：
   * - 右侧放不下时与表头右边缘对齐，仍不够就贴视口右边；
   * - 下方放不下时翻转到表头上方；上下都不够时取空间较大的一侧，
   *   并把值列表的最大高度压缩到可用空间内，保证操作按钮始终可见。
   *
   * @param {HTMLElement} th
   */
  position(th) {
    const GAP = 4;
    const thRect = th.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = document.documentElement.clientHeight;

    // 先复位高度约束，避免上一次的收缩值影响本次测量
    this.valueList.style.maxHeight = '';

    const panelWidth = this.element.offsetWidth;
    let left = thRect.left;

    if (left + panelWidth > viewportWidth - GAP) {
      left = thRect.right - panelWidth;
    }
    left = Math.max(GAP, Math.min(left, viewportWidth - panelWidth - GAP));

    const spaceBelow = viewportHeight - thRect.bottom - GAP;
    const spaceAbove = thRect.top - GAP;
    const panelHeight = this.element.offsetHeight;

    let top;

    if (panelHeight <= spaceBelow) {
      top = thRect.bottom;
    } else if (panelHeight <= spaceAbove) {
      top = thRect.top - panelHeight;
    } else {
      // 上下都放不下：选空间较大的一侧，并压缩值列表高度
      const openBelow = spaceBelow >= spaceAbove;
      const available = openBelow ? spaceBelow : spaceAbove;
      // 面板除值列表以外的固定高度（条件区/搜索/全选/按钮等）
      const chromeHeight = panelHeight - this.valueList.offsetHeight;
      const listMaxHeight = Math.max(available - chromeHeight, MIN_VALUE_LIST_HEIGHT);

      this.valueList.style.maxHeight = `${listMaxHeight}px`;
      top = openBelow ? thRect.bottom : Math.max(GAP, thRect.top - this.element.offsetHeight);
    }

    this.element.style.top = `${Math.max(GAP, top)}px`;
    this.element.style.left = `${left}px`;
  }

  hide() {
    this.element.style.display = 'none';
    // 复位被压缩的值列表高度，下次打开重新按可用空间计算
    this.valueList.style.maxHeight = '';
    this.column = -1;
  }

  isVisible() {
    return this.element.style.display !== 'none';
  }

  /**
   * 条件下拉变化：按算子所需参数个数显示输入框。
   */
  onConditionChange() {
    const name = this.conditionSelect.value;

    this.draft.condition = name === 'none' ? null : { name, args: [] };
    this.syncArgInputs();
  }

  syncArgInputs() {
    const name = this.conditionSelect.value;
    const meta = CONDITION_OPTIONS.filter(item => item.name === name)[0];
    const argCount = meta ? meta.argCount : 0;
    const args = (this.draft.condition && this.draft.condition.args) || [];

    this.argInputs.forEach((input, index) => {
      if (index < argCount) {
        input.style.display = '';
        input.value = args[index] === undefined ? '' : args[index];
      } else {
        input.style.display = 'none';
        input.value = '';
      }
    });
  }

  /**
   * 渲染值复选列表（受搜索关键字过滤）。
   */
  renderValueList() {
    const { valueList } = this;

    valueList.innerHTML = '';

    const visibleValues = this.distinctValues.filter((value) => {
      if (this.keyword === '') {
        return true;
      }

      const label = value === '' ? BLANK_LABEL : value;

      return label.toLowerCase().indexOf(this.keyword) !== -1;
    });

    // selectedValues 为 null 表示全选
    const selected = this.draft.selectedValues;

    visibleValues.forEach((value) => {
      const row = document.createElement('label');

      row.className = 'htFilterValueItem';

      const checkbox = document.createElement('input');

      checkbox.type = 'checkbox';
      checkbox.checked = selected === null || selected.indexOf(value) !== -1;
      checkbox.addEventListener('change', () => this.onValueToggle(value, checkbox.checked));

      const text = document.createElement('span');

      text.textContent = value === '' ? BLANK_LABEL : value;
      // 长文本用 title 兜底，避免面板被撑宽
      text.title = text.textContent;

      row.appendChild(checkbox);
      row.appendChild(text);
      valueList.appendChild(row);
    });

    if (!visibleValues.length) {
      const emptyTip = document.createElement('div');

      emptyTip.className = 'htFilterValueEmpty';
      emptyTip.textContent = '无匹配项';
      valueList.appendChild(emptyTip);
    }

    this.syncSelectAllState();
  }

  /**
   * 勾选/取消某个值。
   */
  onValueToggle(value, checked) {
    // 从"全选"状态首次取消时，需先物化成完整数组
    if (this.draft.selectedValues === null) {
      this.draft.selectedValues = this.distinctValues.slice();
    }

    const index = this.draft.selectedValues.indexOf(value);

    if (checked && index === -1) {
      this.draft.selectedValues.push(value);
    } else if (!checked && index !== -1) {
      this.draft.selectedValues.splice(index, 1);
    }

    this.syncSelectAllState();
  }

  /**
   * 全选框：对"当前可见的值"生效（与 Excel 行为一致）。
   */
  onSelectAllChange() {
    const checked = this.selectAllCheckbox.checked;

    if (checked) {
      if (this.keyword === '') {
        // 无搜索时直接回到"全选"语义
        this.draft.selectedValues = null;
      } else {
        if (this.draft.selectedValues === null) {
          this.draft.selectedValues = this.distinctValues.slice();
        }
        this.getVisibleValues().forEach((value) => {
          if (this.draft.selectedValues.indexOf(value) === -1) {
            this.draft.selectedValues.push(value);
          }
        });
      }
    } else {
      if (this.draft.selectedValues === null) {
        this.draft.selectedValues = this.distinctValues.slice();
      }

      const visible = this.getVisibleValues();

      this.draft.selectedValues = this.draft.selectedValues
        .filter(value => visible.indexOf(value) === -1);
    }

    this.renderValueList();
  }

  getVisibleValues() {
    return this.distinctValues.filter((value) => {
      if (this.keyword === '') {
        return true;
      }

      const label = value === '' ? BLANK_LABEL : value;

      return label.toLowerCase().indexOf(this.keyword) !== -1;
    });
  }

  syncSelectAllState() {
    const visible = this.getVisibleValues();
    const selected = this.draft.selectedValues;

    if (selected === null) {
      this.selectAllCheckbox.checked = true;
      this.selectAllCheckbox.indeterminate = false;

      return;
    }

    const checkedCount = visible.filter(value => selected.indexOf(value) !== -1).length;

    this.selectAllCheckbox.checked = checkedCount > 0 && checkedCount === visible.length;
    this.selectAllCheckbox.indeterminate = checkedCount > 0 && checkedCount < visible.length;
  }

  /**
   * 收集面板当前状态，写入插件。
   *
   * @returns {Object}
   */
  collectState() {
    const name = this.conditionSelect.value;
    let condition = null;

    if (name !== 'none') {
      const meta = CONDITION_OPTIONS.filter(item => item.name === name)[0];
      const argCount = meta ? meta.argCount : 0;
      const args = [];

      for (let i = 0; i < argCount; i++) {
        args.push(this.argInputs[i].value);
      }

      condition = { name, args };
    }

    let selectedValues = this.draft.selectedValues;

    // 勾选了全部值等同于未筛选，归一成 null 以减少状态体积
    if (Array.isArray(selectedValues)
      && selectedValues.length === this.distinctValues.length) {
      selectedValues = null;
    }

    return { selectedValues, condition };
  }

  destroy() {
    if (this.element && this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
    this.element = null;
  }
}

export default FilterPanel;
